import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function CloudEntityActivityLogs({ data }: { data: any }) {
    if (!data || typeof data !== "object") {
        return (
            <div className="p-4 text-xs text-muted-foreground italic">
                No details available.
            </div>
        );
    }

    return (
        <div className="p-4 bg-muted/30 border-t border-border/40 rounded-b-md text-xs font-mono">
            <JsonTree data={data} depth={0} />
        </div>
    );
}

/**
 * Recursive tree view for nested JSON data.
 */
function JsonTree({
    data,
    depth,
}: {
    data: Record<string, any>;
    depth: number;
}) {
    return (
        <div className="space-y-1">
            {Object.entries(data).map(([key, value]) => (
                <JsonNode key={key} label={key} value={value} depth={depth} />
            ))}
        </div>
    );
}

/**
 * Individual node with optional collapsibility for nested data.
 */
function JsonNode({
    label,
    value,
    depth,
}: {
    label: string;
    value: any;
    depth: number;
}) {
    const [expanded, setExpanded] = useState(false);

    // Check if value is a string that might be JSON
    let parsedValue = value;
    if (typeof value === "string") {
        try {
            const trimmed = value.trim();
            if ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
                (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
                parsedValue = JSON.parse(value);
            }
        } catch (e) {
            // If parsing fails, keep the original string value
            parsedValue = value;
        }
    }

    const isObject = typeof parsedValue === "object" && parsedValue !== null;

    return (
        <div
            className="flex flex-col"
            style={{ marginLeft: depth * 16 }}
        >
            <div
                className={`flex items-start gap-1 ${isObject ? "cursor-pointer" : ""
                    }`}
                onClick={() => isObject && setExpanded(!expanded)}
            >
                {isObject ? (
                    expanded ? (
                        <ChevronUp className="w-3 h-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                    ) : (
                        <ChevronDown className="w-3 h-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                    )
                ) : (
                    <div className="w-3 h-3 flex-shrink-0" />
                )}
                <span className="font-semibold text-foreground">{label}:</span>
                {!isObject && (
                    <span className="text-muted-foreground break-all whitespace-pre-wrap">
                        {String(parsedValue)}
                    </span>
                )}
            </div>

            {isObject && expanded && (
                <div className="pl-4 mt-1 border-l border-border/30">
                    {Array.isArray(parsedValue) ? (
                        parsedValue.length > 0 ? (
                            parsedValue.map((item, idx) => (
                                <JsonNode
                                    key={idx}
                                    label={`[${idx}]`}
                                    value={item}
                                    depth={depth + 1}
                                />
                            ))
                        ) : (
                            <span className="text-muted-foreground italic">[empty]</span>
                        )
                    ) : (
                        <JsonTree data={parsedValue} depth={depth + 1} />
                    )}
                </div>
            )}
        </div>
    );
}
